import React, { useContext, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { MapContext } from './mapContext'
import { FeatureCollection, Geometry } from 'geojson'
import { ThemeProvider, useTheme } from 'styled-components'
import { FeatureWithInfoAndStyle } from '~/packages/sentinel/statsAndGeoToFeatureCollection.util'

interface GeoJSONProps {
  geoJson: FeatureCollection<Geometry | null>
  idPropertyName?: string
  dataMapStyle?:
    | ((feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions)
    | google.maps.Data.StyleOptions
  renderInfoWindow?: (props: { feature: FeatureWithInfoAndStyle }) => JSX.Element
  onClick?: (event: google.maps.Data.MouseEvent) => void
}

export default function GeoJSON({
  geoJson,
  idPropertyName = 'id',
  dataMapStyle = (feature: google.maps.Data.Feature) => ({
    fillColor: feature.getProperty('fillColor') as string,
    fillOpacity: feature.getProperty('fillOpacity') as number,
    strokeColor: feature.getProperty('strokeColor') as string,
    strokeOpacity: feature.getProperty('strokeOpacity') as number,
    strokeWeight: feature.getProperty('strokeWeight') as number,
    zIndex: feature.getProperty('zIndex') as number,
  }),
  renderInfoWindow,
  onClick,
}: GeoJSONProps) {
  const mapContext = useContext(MapContext)
  const theme = useTheme()

  if (!mapContext) {
    throw new Error('GeoJson must be used within a Map')
  }

  const { map, google, mapType } = mapContext

  useEffect(() => {
    if (!map || !google) {
      return
    }

    const layer = new google.maps.Data()
    layer.addGeoJson(geoJson, { idPropertyName })

    const infoWindow = new google.maps.InfoWindow()

    layer.addListener('click', function (event: google.maps.Data.MouseEvent) {
      if (onClick) {
        onClick(event)
      }
      if (!renderInfoWindow) {
        return
      }

      // Get turf Feature
      const feature = geoJson.features.find(
        current =>
          current.properties?.[idPropertyName] === event.feature.getProperty(idPropertyName),
      ) as FeatureWithInfoAndStyle
      const div = document.createElement('div')
      div.className = 'prix-info-window'
      const root = createRoot(div)
      root.render(<ThemeProvider theme={theme}>{renderInfoWindow({ feature })}</ThemeProvider>)

      infoWindow.setContent(div)
      infoWindow.setPosition(event.latLng)
      infoWindow.open(map)
      infoWindow.addListener('closeclick', () => {
        root.unmount()
      })
    })

    if (dataMapStyle) {
      layer.setStyle(dataMapStyle)
    }
    layer.setMap(map)

    return () => {
      infoWindow.close()
      layer.setMap(null)
    }
  }, [
    map,
    google,
    geoJson,
    idPropertyName,
    dataMapStyle,
    mapType,
    renderInfoWindow,
    theme,
    onClick,
  ])

  return null
}
